import { useCallback, useEffect, useRef, useState } from 'react';
import type { SigningField } from '../types/fields';
import { getMediaDimensionsFromPageEntry } from '../utils/signingFieldState';
import { normalizeRotation } from '../utils/pageRotation';
import {
  fetchWorkingPdfBlob,
  mergePdf,
  mergePdfDirect,
  mutatePdfPages,
  mutatePdfPagesDirect,
  uploadPdf,
  type DocumentMutationResponse,
} from '../utils/api';
import {
  remapFieldPageIndices,
  remapFieldsOnPageDuplicate,
  remapFieldsOnPageRotation,
  remapFieldsOnReorder,
} from '../utils/remapFields';

interface UsePageOperationsOptions {
  uploadedFileId: string | null;
  pdfFile: File | null;
  pageSizes: Array<{ width: number; height: number; rotation?: number }>;
  fields: SigningField[];
  replaceFields: (next: SigningField[]) => void;
  applyServerDocument: (
    fileId: string,
    meta: { pageCount: number; pageSizes: Array<{ width: number; height: number; rotation?: number }> }
  ) => void;
  setPdfFileFromBlob: (blob: Blob, name: string) => void;
  originalFileName: string;
  setStatusMessage: (msg: string) => void;
  setErrorMessage: (msg: string) => void;
  /**
   * Read-only accessor for usePdfSession's monotonic load generation. Captured at the
   * start of a page op and re-checked after the async server round-trip so a stale
   * result on an OLD document cannot clobber a NEW session loaded mid-flight.
   * Optional for back-compat: when omitted the stale-session guard is a no-op.
   * SigningWorkspace must pass `pdf.getLoadGeneration` to activate the guard.
   */
  getLoadGeneration?: () => number;
  /**
   * History-free field replace for the OPTIMISTIC reorder step so one reorder produces
   * exactly ONE undo entry (the authoritative remap inside applyMutation pushes the
   * single history entry). When omitted, falls back to `replaceFields`.
   * SigningWorkspace must wire this from useSigningFields to fully fix the double-undo.
   */
  replaceFieldsNoHistory?: (next: SigningField[]) => void;
}

async function applyMutation(
  result: DocumentMutationResponse,
  fieldsSnapshot: SigningField[],
  replaceFields: (next: SigningField[]) => void,
  applyServerDocument: UsePageOperationsOptions['applyServerDocument'],
  setPdfFileFromBlob: UsePageOperationsOptions['setPdfFileFromBlob'],
  originalFileName: string,
  pageSizes: UsePageOperationsOptions['pageSizes'],
  rotateMeta?: {
    pageIndex: number;
    oldRotation: number;
    newRotation: number;
    /** PRE-rotation media dimensions captured before the server mutation */
    preMediaWidth: number;
    preMediaHeight: number;
  },
  duplicateMeta?: {
    duplicatedPageIndex: number;
  },
  /** Latest fields at apply time — keeps fields placed/edited while the server request was in flight. */
  getLiveFields?: () => SigningField[],
  /**
   * M1: stale-session guard. `captured` is the load generation at op start; `current()`
   * reads the live generation. If they differ when the async work resolves, a new
   * document was loaded (or the session cleared) mid-flight — bail without touching
   * session/fields so the stale result cannot clobber the new session.
   */
  generationGuard?: { captured: number; current: () => number },
  /**
   * When false, fields placed DURING the op (preserved) are appended WITHOUT the
   * page-index remap. Used by reorder: its optimistic step already moved live
   * fields into the new page order, so a field placed mid-flight is ALREADY in
   * new-index space and re-applying the server pageIndexMap would double-remap it.
   * Defaults true for delete/duplicate/rotate, which do not pre-remap live fields.
   */
  remapPreserved: boolean = true
) {
  // Fetch working PDF bytes first so a failed download never leaves fields and
  // document metadata out of sync with the local pdfFile blob.
  const blob = await fetchWorkingPdfBlob(result.fileId, result.sourceUrl);

  // M1: a NEW document was loaded (or the session cleared) while this op was in flight.
  // Abort before mutating any session/field state.
  if (generationGuard && generationGuard.captured !== generationGuard.current()) {
    return;
  }

  // M3: the same page-index/rotation/duplicate remap applied to the snapshot fields
  // must also be applied to fields placed DURING the op (live but absent from the
  // snapshot); otherwise they keep their pre-op pageIndex and land on the wrong page.
  const applyRemap = (input: SigningField[]): SigningField[] => {
    let out = duplicateMeta
      ? remapFieldsOnPageDuplicate(
          input,
          duplicateMeta.duplicatedPageIndex,
          result.pageIndexMap
        )
      : remapFieldPageIndices(input, result.pageIndexMap);
    if (rotateMeta) {
      // BUG-03: use PRE-rotation media dimensions so fields don't jump.
      // result.pageSizes already reflects post-rotation dimensions (swapped for 90/270),
      // which would corrupt the coordinate transform. We captured the originals in rotatePage.
      out = remapFieldsOnPageRotation(
        out,
        rotateMeta.pageIndex,
        rotateMeta.preMediaWidth,
        rotateMeta.preMediaHeight,
        rotateMeta.oldRotation,
        rotateMeta.newRotation
      );
    }
    return out;
  };

  const snapshotIds = new Set(fieldsSnapshot.map((f) => f.id));
  let nextFields = applyRemap(fieldsSnapshot);
  const preserved = (getLiveFields?.() ?? []).filter((f) => !snapshotIds.has(f.id));
  if (preserved.length > 0) {
    // M3: remap the in-flight-placed fields through the SAME transform before
    // appending — unless the caller already remapped live fields (reorder).
    nextFields = [...nextFields, ...(remapPreserved ? applyRemap(preserved) : preserved)];
  }
  replaceFields(nextFields);
  applyServerDocument(result.fileId, {
    pageCount: result.pageCount,
    pageSizes: result.pageSizes,
  });
  setPdfFileFromBlob(blob, originalFileName);
}

export function usePageOperations(opts: UsePageOperationsOptions) {
  const {
    uploadedFileId,
    pdfFile,
    pageSizes,
    fields,
    replaceFields,
    applyServerDocument,
    setPdfFileFromBlob,
    originalFileName,
    setStatusMessage,
    setErrorMessage,
    getLoadGeneration,
    replaceFieldsNoHistory,
  } = opts;

  // M1: build the stale-session guard captured at op start. No-op when the host did
  // not wire getLoadGeneration (back-compat).
  const makeGenerationGuard = useCallback(() => {
    if (!getLoadGeneration) return undefined;
    return { captured: getLoadGeneration(), current: getLoadGeneration };
  }, [getLoadGeneration]);

  // A2: serialize page operations. Rapid clicks would otherwise launch parallel
  // ops that each capture the SAME pre-op pdfFile/uploadedFileId/fields closure;
  // the later-resolving response would win applyServerDocument + setPdfFileFromBlob
  // and silently discard the other op's result. The ref is the synchronous guard
  // (state updates are async and can't gate a same-tick second click); the state
  // mirror drives the disabled UI.
  const pageOpInFlight = useRef(false);
  const fieldsRef = useRef(fields);
  const [isPageOpRunning, setIsPageOpRunning] = useState(false);

  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);

  const mergeFromFile = useCallback(
    async (appendFile: File) => {
      if (!uploadedFileId && !pdfFile) {
        setErrorMessage('Upload a PDF first.');
        return;
      }
      // A2: block a second op while one is in flight.
      if (pageOpInFlight.current) return;
      pageOpInFlight.current = true;
      setIsPageOpRunning(true);
      const fieldsSnapshot = fieldsRef.current;
      const generationGuard = makeGenerationGuard();
      try {
        setStatusMessage('Merging PDF…');
        let result: DocumentMutationResponse;
        if (pdfFile) {
          result = await mergePdfDirect(pdfFile, appendFile);
        } else {
          const uploaded = await uploadPdf(appendFile);
          result = await mergePdf(uploadedFileId!, uploaded.fileId);
        }
        await applyMutation(
          result,
          fieldsSnapshot,
          replaceFields,
          applyServerDocument,
          setPdfFileFromBlob,
          originalFileName,
          pageSizes,
          undefined,
          undefined,
          () => fieldsRef.current,
          generationGuard
        );
        setStatusMessage(`Merged — now ${result.pageCount} pages.`);
      } catch (e) {
        setErrorMessage(e instanceof Error ? e.message : 'Merge failed');
      } finally {
        pageOpInFlight.current = false;
        setIsPageOpRunning(false);
      }
    },
    [
      uploadedFileId,
      pdfFile,
      replaceFields,
      applyServerDocument,
      setPdfFileFromBlob,
      originalFileName,
      pageSizes,
      setStatusMessage,
      setErrorMessage,
      makeGenerationGuard,
    ]
  );

  const runPageOp = useCallback(
    async (
      operation: Record<string, unknown>,
      rotateMeta?: Parameters<typeof applyMutation>[7],
      duplicateMeta?: Parameters<typeof applyMutation>[8]
    ) => {
      if (!uploadedFileId && !pdfFile) return;
      // A2: block a second op while one is in flight.
      if (pageOpInFlight.current) return;
      pageOpInFlight.current = true;
      setIsPageOpRunning(true);
      const fieldsSnapshot = fieldsRef.current;
      const generationGuard = makeGenerationGuard();
      try {
        const result = pdfFile
          ? await mutatePdfPagesDirect(pdfFile, operation)
          : await mutatePdfPages(uploadedFileId!, operation);
        await applyMutation(
          result,
          fieldsSnapshot,
          replaceFields,
          applyServerDocument,
          setPdfFileFromBlob,
          originalFileName,
          pageSizes, // still passed for future use; rotation uses rotateMeta.preMedia* instead
          rotateMeta,
          duplicateMeta,
          () => fieldsRef.current,
          generationGuard
        );
      } catch (e) {
        setErrorMessage(e instanceof Error ? e.message : 'Page operation failed');
      } finally {
        pageOpInFlight.current = false;
        setIsPageOpRunning(false);
      }
    },
    [
      uploadedFileId,
      pdfFile,
      replaceFields,
      applyServerDocument,
      setPdfFileFromBlob,
      originalFileName,
      pageSizes,
      setErrorMessage,
      makeGenerationGuard,
    ]
  );

  const deletePage = useCallback(
    (pageIndex: number) => runPageOp({ action: 'delete', pageIndex }),
    [runPageOp]
  );

  const duplicatePage = useCallback(
    (pageIndex: number) =>
      runPageOp({ action: 'duplicate', pageIndex }, undefined, { duplicatedPageIndex: pageIndex }),
    [runPageOp]
  );

  const insertBlank = useCallback(
    (afterIndex?: number) => {
      const coerced =
        afterIndex === undefined || afterIndex === null
          ? undefined
          : Number(afterIndex);
      if (
        coerced !== undefined &&
        (!Number.isInteger(coerced) || coerced < -1)
      ) {
        setErrorMessage('Invalid page index for insert');
        return;
      }
      return runPageOp({ action: 'insert-blank', afterIndex: coerced });
    },
    [runPageOp, setErrorMessage]
  );

  const rotatePage = useCallback(
    (pageIndex: number, direction: 'cw' | 'ccw' = 'cw') => {
      const step = direction === 'cw' ? 90 : -90;
      const pageSize = pageSizes[pageIndex];
      const oldRotation = pageSize?.rotation ?? 0;
      const newRotation = normalizeRotation(oldRotation + step);
      // BUG-03: capture PRE-rotation media dimensions NOW (before the server mutates the doc).
      // After 90/270° rotation the server swaps width/height in pageSizes, so we must not read
      // result.pageSizes afterward for the remap — we pass these snapshots through rotateMeta.
      //
      // A3: pageSizes may be server MEDIA metadata (pre pdf.js sync) or pdf.js VIEWPORT
      // sizes (visual:true after syncViewerPageSizes). remapFieldsOnPageRotation always
      // needs PRE-rotation media dimensions — getMediaDimensionsFromPageEntry handles both.
      const { width: preMediaWidth, height: preMediaHeight } = getMediaDimensionsFromPageEntry(
        pageSize ?? { width: 0, height: 0, rotation: oldRotation }
      );
      return runPageOp(
        { action: 'rotate', pageIndex, degrees: step },
        { pageIndex, oldRotation, newRotation, preMediaWidth, preMediaHeight }
      );
    },
    [runPageOp, pageSizes]
  );

  /**
   * Reorder pages: order[newPosition] = oldIndex.
   * Calls the server's { action: 'reorder', order } mutation, then remaps field
   * pageIndices so each field follows its page to its new position.
   *
   * History-aware: uses runPageOp which goes through applyMutation + replaceFields,
   * consistent with deletePage/duplicatePage/rotatePage.
   *
   * Note: the server's pageIndexMap from a reorder response already encodes
   * old->new in the standard format, but we compute the remap locally from
   * the 'order' array BEFORE the server call completes (client-optimistic fields
   * approach) via the standard applyMutation path with remapFieldsOnReorder
   * applied pre-flight inside our override of applyMutation.
   *
   * Actually, we delegate to runPageOp so the server response's pageIndexMap
   * drives remapFieldPageIndices automatically; then we additionally apply
   * remapFieldsOnReorder with the original 'order' array to handle the case
   * where the server returns an identity-like pageIndexMap for reorder
   * (some implementations do; ours may differ). We apply the reorder remap
   * directly since the server pdfDocument.service mutatePdfPages for
   * { action: 'reorder' } returns a fresh pageIndexMap that is exactly the
   * identity (0->0, 1->1, …) on the NEW document — meaning the server already
   * embedded the new page order. We must remap fields BEFORE calling runPageOp
   * so we pass a pre-remapped fields snapshot; we do this by calling replaceFields
   * ourselves, then letting runPageOp update pageSizes/fileId via applyServerDocument.
   *
   * Simplest correct approach: remap fields client-side from the order array
   * (which we control and is unambiguous), then call runPageOp which will
   * call applyMutation -> remapFieldPageIndices(identity map) = no-op on already-remapped fields.
   */
  const reorderPages = useCallback(
    async (order: number[]) => {
      if (!uploadedFileId && !pdfFile) return;
      // A2: block a second op while one is in flight (before the optimistic remap,
      // so a blocked reorder cannot mutate fields against a stale closure).
      if (pageOpInFlight.current) return;
      pageOpInFlight.current = true;
      setIsPageOpRunning(true);
      const preReorderFields = fieldsRef.current;
      const generationGuard = makeGenerationGuard();
      // Optimistic client-side remap for INSTANT visual feedback only.
      // NOTE: the server returns a NON-identity pageIndexMap (oldIndex -> newIndex),
      // and applyMutation applies it via remapFieldPageIndices. To avoid a DOUBLE
      // reorder, we pass the ORIGINAL `fields` (not the pre-remapped ones) into
      // applyMutation so the server map is applied exactly once. The optimistic
      // remap below and the authoritative server remap produce identical results,
      // so there is no flicker.
      //
      // HISTORY: this optimistic step must NOT create its own undo entry — the
      // authoritative replaceFields inside applyMutation pushes the SINGLE entry for
      // this reorder. Use the history-free setter when wired; only fall back to
      // replaceFields (which would create a second, redundant undo entry) when the
      // host has not provided one. Rollback below still restores preReorderFields.
      (replaceFieldsNoHistory ?? replaceFields)(remapFieldsOnReorder(preReorderFields, order));

      try {
        const result = pdfFile
          ? await mutatePdfPagesDirect(pdfFile, { action: 'reorder', order })
          : await mutatePdfPages(uploadedFileId!, { action: 'reorder', order });

        // applyMutation handles applyServerDocument + setPdfFileFromBlob and applies
        // the server's pageIndexMap to the ORIGINAL fields (single, correct remap).
        await applyMutation(
          result,
          preReorderFields,
          replaceFields,
          applyServerDocument,
          setPdfFileFromBlob,
          originalFileName,
          pageSizes,
          undefined,
          undefined,
          () => fieldsRef.current,
          generationGuard,
          // reorder already optimistically remapped live fields → don't re-remap preserved.
          false
        );
      } catch (e) {
        // M1: if a new document was loaded (or the session cleared) mid-flight, this
        // op is stale — do NOT roll back fields, or we would clobber the new session's
        // fields with the old document's pre-reorder snapshot.
        if (generationGuard && generationGuard.captured !== generationGuard.current()) {
          return;
        }
        // On error, roll back fields to the pre-remap state (ref, not stale closure).
        // History-free to match the optimistic step (no stray undo entry on failure).
        (replaceFieldsNoHistory ?? replaceFields)(preReorderFields);
        setErrorMessage(e instanceof Error ? e.message : 'Page reorder failed');
      } finally {
        pageOpInFlight.current = false;
        setIsPageOpRunning(false);
      }
    },
    [
      uploadedFileId,
      pdfFile,
      replaceFields,
      replaceFieldsNoHistory,
      applyServerDocument,
      setPdfFileFromBlob,
      originalFileName,
      pageSizes,
      setErrorMessage,
      makeGenerationGuard,
    ]
  );

  return {
    mergeFromFile,
    deletePage,
    duplicatePage,
    insertBlank,
    rotatePage,
    reorderPages,
    isPageOpRunning,
  };
}